import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useRouteSeo } from "../lib/seo";

const AboutPage = () => {
  useRouteSeo("/about");

  return (
    <main className="pt-20 bg-[#F9F8F6]" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-40 relative overflow-hidden">
        <div className="absolute -right-20 top-20 font-heading text-[24rem] font-light italic text-[#EBE6DD]/60 leading-none pointer-events-none select-none hidden lg:block">
          about
        </div>
        <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px bg-[#C85A43] w-16" />
              <p className="text-sm md:text-base uppercase tracking-[0.3em] font-bold text-[#C85A43]">
                About
              </p>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-[6.5rem] font-semibold tracking-[-0.03em] leading-[0.95] text-[#1C1917] mb-10">
              Built on trust,{" "}
              <span className="italic font-light text-[#C85A43]">
                driven by excellence.
              </span>
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-[#3A3634] max-w-2xl font-medium">
              At our core, we believe managing your finances shouldn't feel
              like a trip back to the 1990s.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Section - Editorial */}
      <section className="py-24 md:py-32 bg-white">
        <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-4"
            >
              <div className="sticky top-32">
                <div className="relative overflow-hidden rounded-sm">
                  <img
                    src="/naira_marapyan.webp"
                    alt="Naira Marapyan, CPA, MSA - Founder"
                    className="w-full max-w-xs object-cover"
                  />
                </div>
                <div className="mt-6">
                  <p className="font-heading text-2xl text-[#1C1917]">
                    Naira Marapyan
                  </p>
                  <p className="text-sm text-[#78716C] tracking-wider mt-1">
                    CPA · MSA · Founder
                  </p>
                </div>
                <div className="mt-8 p-6 bg-[#F9F8F6] border-l-2 border-[#C85A43]">
                  <p className="font-heading text-lg italic text-[#1C1917] leading-relaxed">
                    "Our mission is to demystify finance and empower our
                    clients to make informed decisions."
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-8"
            >
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px bg-[#C85A43] w-12" />
                <p className="text-sm md:text-base uppercase tracking-[0.3em] font-bold text-[#C85A43]">
                  The Story
                </p>
              </div>

              <p className="font-heading text-3xl md:text-4xl font-light text-[#1C1917] leading-tight mb-12">
                We are a{" "}
                <span className="italic text-[#C85A43]">100% virtual</span>{" "}
                CPA firm based in California, founded on a simple premise:
                growing businesses and high-earning professionals deserve
                proactive financial strategy, modern technology, and clear
                communication—without the traditional accounting hassle.
              </p>

              <div className="space-y-6 text-lg text-[#3A3634] leading-relaxed">
                <p>
                  <span className="text-[#C85A43] font-semibold">
                    Marapyan CPA, Inc
                  </span>{" "}
                  was established in 2026 by Naira Marapyan, a licensed
                  Certified Public Accountant in the state of California.
                  Built on a foundation of deep expertise and modern practice,
                  our firm is dedicated to providing exceptional financial
                  services with a personal touch.
                </p>
                <p>
                  Naira holds a Master's degree in Accounting and brings over
                  15 years of accounting experience to every client
                  engagement—including 10 years working in public accounting.
                  Her career includes work with a diverse range of clients,
                  from individuals and entrepreneurs to established businesses
                  across California.
                </p>
              </div>

              {/* Pull Quote */}
              <blockquote className="my-16 py-8 border-t border-b border-[#EBE6DD]">
                <p className="font-heading text-3xl md:text-4xl italic text-[#1C1917] leading-tight">
                  "We don't just prepare your taxes or balance your books—we
                  become your financial partner, invested in your success."
                </p>
              </blockquote>

              <h3 className="font-heading text-3xl font-semibold text-[#1C1917] mb-6">
                A Modern Approach
              </h3>

              <div className="space-y-6 text-lg text-[#3A3634] leading-relaxed">
                <p>
                  As a fully virtual CPA firm, we've embraced technology to
                  serve our clients more efficiently—without ever losing sight
                  of the human element that sets us apart. You get the
                  convenience of digital access with the trust and clarity
                  that only comes from working with a dedicated professional.
                </p>
                <p>
                  Whether you're launching a tech startup, expanding a medical
                  practice, or scaling a real estate portfolio, we bring
                  high-touch, partner-level expertise straight to your screen.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1C1917] text-white">
        <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px bg-[#C85A43] w-12" />
              <p className="text-sm md:text-base uppercase tracking-[0.3em] font-bold text-[#C85A43]">
                Ready to Begin
              </p>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-white mb-10">
              Let's work{" "}
              <span className="italic font-light text-[#C85A43]">
                together.
              </span>
            </h2>
            <p className="text-[#A8A29E] text-lg max-w-xl mb-10">
              Schedule a complimentary consultation to discuss how we can help
              you achieve your financial goals.
            </p>
            <Link
              to="/#contact"
              className="group inline-flex items-center gap-3 bg-white hover:bg-[#C85A43] text-[#1C1917] hover:text-white rounded-full pl-7 pr-2 py-2 text-sm sm:text-base font-medium tracking-wide transition-all duration-500"
              data-testid="about-cta-btn"
            >
              <span>Schedule a Consultation</span>
              <span className="w-10 h-10 rounded-full bg-[#C85A43] group-hover:bg-white flex items-center justify-center transition-all duration-500">
                <ArrowUpRight className="w-4 h-4 text-white group-hover:text-[#C85A43] transition-colors duration-500" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
